/*
  ACARRi motion layer (phase 6).
  Every effect is optional (theme settings → ACARRi motion) and switches off for
  prefers-reduced-motion. Touch devices skip the cursor and the WebGL hero.
*/
(() => {
  const cfg = window.acarriMotion || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (reduce) return;
  const root = document.documentElement;
  root.classList.add('acarri-motion');

  /* ---------- 1. Smooth scrolling (Lenis) ---------- */
  let lenis = null;
  if (cfg.smooth && window.Lenis && finePointer) {
    lenis = new window.Lenis({ lerp: 0.1, wheelMultiplier: 1, smoothWheel: true });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    // Let drawers, menus and search scroll on their own, and pause while they lock the page.
    const markPrevent = () => document.querySelectorAll('.drawer__inner, .menu-drawer, .predictive-search, .cart-drawer, .acarri-row__track, [data-lenis-prevent]').forEach((el) => el.setAttribute('data-lenis-prevent', ''));
    markPrevent();
    const syncLock = () => {
      const locked = document.body.classList.contains('overflow-hidden') || root.classList.contains('overflow-hidden') || document.body.classList.contains('overflow-hidden-tablet') || document.body.classList.contains('overflow-hidden-desktop');
      if (locked) lenis.stop(); else lenis.start();
      markPrevent();
    };
    new MutationObserver(syncLock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(syncLock).observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- 2. Reveals ---------- */
  const revealSel = [
    '.acarri-display', '.acarri-quote', '.acarri-bigname', '.acarri-hero__kicker', '.acarri-hero__title', '.acarri-hero__side',
    '.acarri-split__figure', '.acarri-row__item', '.acarri-ex__item', '.acarri-history__row', '.acarri-essay__body > *',
    '.acarri-room__label > *', '.acarri-ex__title', '.acarri-inq__title', '.acarri-news__form'
  ].join(',');
  const revealEls = [...document.querySelectorAll(revealSel)];
  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    // Stagger siblings in grids and rows
    const counters = new Map();
    revealEls.forEach((el) => {
      const parent = el.parentElement;
      const i = counters.get(parent) || 0;
      counters.set(parent, i + 1);
      el.style.setProperty('--reveal-delay', Math.min(i, 8) * 70 + 'ms');
      el.classList.add('acarri-reveal');
      io.observe(el);
    });
  }

  /* ---------- 3. Hero: scroll zoom into the strokes ---------- */
  const hero = document.querySelector('.acarri-hero');
  if (hero) {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const r = hero.getBoundingClientRect();
        const p = Math.min(Math.max(-r.top / r.height, 0), 1);
        hero.style.setProperty('--hero-p', p.toFixed(4));
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- 4. Hero: WebGL pastel smear ---------- */
  if (cfg.heroEffect && hero && finePointer) initSmear(hero);

  function initSmear(section) {
    const img = section.querySelector('.acarri-hero__img');
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'acarri-hero__gl';
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl', { premultipliedAlpha: false, antialias: false });
    if (!gl) return;

    const vs = 'attribute vec2 p;varying vec2 v;void main(){v=p*.5+.5;gl_Position=vec4(p,0.,1.);}';
    const fs = [
      'precision mediump float;varying vec2 v;uniform sampler2D t;uniform vec2 res;uniform vec2 isz;uniform vec2 m;uniform vec2 dir;uniform float s;uniform float time;uniform float fy;',
      'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
      'float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}',
      'void main(){',
      '  float ca=res.x/res.y, ia=isz.x/isz.y;',
      '  vec2 sc=ca>ia?vec2(1.,ia/ca):vec2(ca/ia,1.);',
      '  vec2 uv=vec2(v.x,1.-v.y);',
      '  vec2 asp=vec2(ca,1.);',
      '  float d=distance(uv*asp,m*asp);',
      '  float f=smoothstep(.32,0.,d)*s;',
      '  float nz=n(uv*9.+time*.15);',
      '  vec2 off=(dir*.06+(vec2(nz,n(uv*9.-time*.12))-.5)*.035)*f;',
      '  vec2 tuv=(uv-off)*sc+(1.-sc)*vec2(.5,fy);',
      '  vec4 c=texture2D(t,tuv);',
      '  float g=(h(uv*res+time)-.5)*.02;',
      '  gl_FragColor=vec4(c.rgb+g,1.);',
      '}'
    ].join('\n');
    const sh = (type, src) => { const o = gl.createShader(type); gl.shaderSource(o, src); gl.compileShader(o); return gl.getShaderParameter(o, gl.COMPILE_STATUS) ? o : null; };
    const v = sh(gl.VERTEX_SHADER, vs), f = sh(gl.FRAGMENT_SHADER, fs);
    if (!v || !f) return;
    const prog = gl.createProgram();
    gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = (k) => gl.getUniformLocation(prog, k);
    const u = { res: U('res'), isz: U('isz'), m: U('m'), dir: U('dir'), s: U('s'), time: U('time'), fy: U('fy') };
    const posY = parseFloat((getComputedStyle(section).getPropertyValue('--hero-pos').split(' ')[1] || '38%')) / 100 || 0.38;

    const tex = gl.createTexture();
    const start = () => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      } catch (e) { return; }
      gl.uniform2f(u.isz, img.naturalWidth, img.naturalHeight);
      gl.uniform1f(u.fy, posY);
      img.insertAdjacentElement('afterend', canvas);
      resize();
      section.classList.add('has-gl');
      loop();
    };

    let mx = 0.5, my = 0.5, tx = 0.5, ty = 0.5, dx = 0, dy = 0, strength = 0, visible = true, running = false;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = section.clientWidth, h = section.clientHeight;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(u.res, canvas.width, canvas.height);
      draw(performance.now());
    };
    const draw = (t) => {
      gl.uniform2f(u.m, mx, my);
      const len = Math.hypot(dx, dy) || 1;
      gl.uniform2f(u.dir, dx / len, dy / len);
      gl.uniform1f(u.s, strength);
      gl.uniform1f(u.time, t / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    const loop = () => {
      if (running) return;
      running = true;
      const step = (t) => {
        mx += (tx - mx) * 0.12; my += (ty - my) * 0.12;
        strength *= 0.955; // settles back over ~900ms
        draw(t);
        if (visible && (strength > 0.004 || Math.abs(tx - mx) > 0.0005)) requestAnimationFrame(step);
        else running = false;
      };
      requestAnimationFrame(step);
    };
    section.addEventListener('pointermove', (e) => {
      const r = section.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
      dx = nx - tx; dy = ny - ty; tx = nx; ty = ny;
      strength = Math.min(1, strength + Math.hypot(dx, dy) * 6);
      loop();
    });
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) loop(); }).observe(section);
    window.addEventListener('resize', resize);
    if (img.complete && img.naturalWidth) start(); else img.addEventListener('load', start, { once: true });
  }

  /* ---------- 5. Ink blot on click ---------- */
  if (cfg.ink) {
    const svgNS = 'http://www.w3.org/2000/svg';
    let n = 0;
    const blob = (r, pts, jitter) => {
      // An irregular closed shape: a circle pushed in and out at random points.
      const a = [];
      for (let i = 0; i < pts; i++) {
        const t = (i / pts) * Math.PI * 2;
        const rr = r * (1 - jitter / 2 + Math.random() * jitter);
        a.push([Math.cos(t) * rr, Math.sin(t) * rr]);
      }
      let d = `M${a[0][0].toFixed(1)},${a[0][1].toFixed(1)}`;
      for (let i = 0; i < pts; i++) {
        const p0 = a[i], p1 = a[(i + 1) % pts];
        const mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2;
        d += ` Q${p0[0].toFixed(1)},${p0[1].toFixed(1)} ${mx.toFixed(1)},${my.toFixed(1)}`;
      }
      return d + 'Z';
    };
    document.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      const size = 120;
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('class', 'acarri-ink');
      svg.setAttribute('viewBox', `${-size} ${-size} ${size * 2} ${size * 2}`);
      svg.setAttribute('aria-hidden', 'true');
      svg.style.left = e.clientX + 'px';
      svg.style.top = e.clientY + 'px';
      const id = 'acarriInk' + (n++);
      let paths = `<filter id="${id}"><feTurbulence type="fractalNoise" baseFrequency="0.06" numOctaves="2" seed="${Math.floor(Math.random() * 99)}"/><feDisplacementMap in="SourceGraphic" scale="14"/></filter><g filter="url(#${id})">`;
      paths += `<path d="${blob(34, 11, 0.5)}"/>`;
      const drops = 4 + Math.floor(Math.random() * 4);
      for (let i = 0; i < drops; i++) {
        const ang = Math.random() * Math.PI * 2, dist = 44 + Math.random() * 40, r = 3 + Math.random() * 7;
        paths += `<path transform="translate(${(Math.cos(ang) * dist).toFixed(1)} ${(Math.sin(ang) * dist).toFixed(1)})" d="${blob(r, 7, 0.4)}"/>`;
      }
      svg.innerHTML = paths + '</g>';
      document.body.appendChild(svg);
      svg.addEventListener('animationend', () => svg.remove(), { once: true });
    }, { passive: true });
  }

  /* ---------- 6. Floating pastel dust ---------- */
  if (cfg.floaters) {
    const c = document.createElement('canvas');
    c.className = 'acarri-floaters';
    c.setAttribute('aria-hidden', 'true');
    document.body.appendChild(c);
    const ctx = c.getContext('2d');
    const colors = ['237,230,218', '255,107,26', '214,160,120', '150,170,190', '190,150,200'];
    let w, h, dpr, motes = [], visible = !document.hidden;
    const make = () => {
      const count = Math.round(Math.min(60, (w * h) / 26000) * (finePointer ? 1 : 0.5));
      motes = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        r: 0.6 + Math.random() * 2.2,
        vx: (Math.random() - 0.5) * 0.12, vy: -0.05 - Math.random() * 0.18,
        a: 0.08 + Math.random() * 0.3, ph: Math.random() * Math.PI * 2,
        c: colors[Math.floor(Math.random() * colors.length)]
      }));
    };
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      w = window.innerWidth; h = window.innerHeight;
      c.width = w * dpr; c.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      make();
    };
    const frame = (t) => {
      if (!visible) return;
      ctx.clearRect(0, 0, w, h);
      for (const m of motes) {
        m.x += m.vx + Math.sin(t / 3000 + m.ph) * 0.08;
        m.y += m.vy;
        if (m.y < -10) { m.y = h + 10; m.x = Math.random() * w; }
        if (m.x < -10) m.x = w + 10; else if (m.x > w + 10) m.x = -10;
        const tw = 0.6 + 0.4 * Math.sin(t / 1400 + m.ph);
        ctx.beginPath();
        ctx.fillStyle = `rgba(${m.c},${(m.a * tw).toFixed(3)})`;
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
      requestAnimationFrame(frame);
    };
    document.addEventListener('visibilitychange', () => { visible = !document.hidden; if (visible) requestAnimationFrame(frame); });
    window.addEventListener('resize', resize);
    resize();
    requestAnimationFrame(frame);
  }

  /* ---------- 7. Magnetic buttons ---------- */
  if (cfg.magnetic && finePointer) {
    document.querySelectorAll('.button, .acarri-chip--strong').forEach((b) => {
      b.addEventListener('pointermove', (e) => {
        const r = b.getBoundingClientRect();
        const mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        const my = ((e.clientY - r.top) / r.height - 0.5) * 2;
        b.style.translate = `${(mx * 6).toFixed(1)}px ${(my * 4).toFixed(1)}px`;
      });
      b.addEventListener('pointerleave', () => { b.style.translate = ''; });
    });
  }

  /* ---------- 8. Synth-wave titles: letters ripple out from the cursor ---------- */
  if (cfg.wave !== false && finePointer) {
    const pairs = [
      ['.acarri-ex__card', '.acarri-ex__name'],
      ['.acarri-row__card', '.acarri-row__name'],
      ['.acarri-rooms__door', '.acarri-rooms__name'],
      ['.acarri-room__step', '.acarri-room__step-title'],
      ['.acarri-pop', '.acarri-pop__label']
    ];
    pairs.forEach(([cardSel, titleSel]) => {
      document.querySelectorAll(cardSel).forEach((card) => {
        const title = card.querySelector(titleSel);
        if (!title || title.dataset.wave) return;
        const text = title.textContent;
        title.dataset.wave = '1';
        title.textContent = '';
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = text;
        const vis = document.createElement('span');
        vis.setAttribute('aria-hidden', 'true');
        vis.className = 'acarri-wave';
        // Letters are grouped by word so titles never break mid-word.
        const letters = [];
        text.trim().split(/\s+/).forEach((part, wi) => {
          if (wi > 0) vis.appendChild(document.createTextNode(' '));
          const word = document.createElement('span');
          word.className = 'acarri-wave__w';
          [...part].forEach((ch) => {
            const l = document.createElement('span');
            l.className = 'acarri-wave__l';
            l.textContent = ch;
            word.appendChild(l);
            letters.push(l);
          });
          vis.appendChild(word);
        });
        title.append(sr, vis);
        card.addEventListener('pointerenter', (e) => {
          // find the letter nearest the cursor and ripple outward from it
          let center = 0, best = Infinity;
          letters.forEach((l, i) => {
            const r = l.getBoundingClientRect();
            const d = Math.abs(r.left + r.width / 2 - e.clientX) + Math.abs(r.top + r.height / 2 - e.clientY) * 0.5;
            if (d < best) { best = d; center = i; }
          });
          letters.forEach((l, i) => l.style.setProperty('--wd', Math.abs(i - center) * 45 + 'ms'));
          vis.classList.add('is-on');
        });
        card.addEventListener('pointerleave', () => vis.classList.remove('is-on'));
      });
    });
  }
})();
